import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MapPin, Navigation, Plus, Trash2, ArrowRight, Clock, Map as MapIcon, CheckCircle2, Search, Loader2, GripVertical, Camera, Layers, CloudSun, AlertTriangle, Route, ChevronLeft, ChevronRight, RotateCcw, Bed, Utensils, Fuel, Landmark, Compass, Building2, Sparkles, Car, Train, Bus, Plane, Footprints, Bike } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { useMemory } from '../hooks/useMemory';
import { useTheme } from '../context/ThemeContext';

// --- STATIC CONFIGURATIONS ---

const mapLayouts = {
  street: { version: 8, sources: { 'osm-tiles': { type: 'raster', tiles: ['https://a.tile.openstreetmap.org/{z}/{x}/{y}.png'], tileSize: 256, attribution: '© OpenStreetMap' } }, layers: [{ id: 'osm-tiles-layer', type: 'raster', source: 'osm-tiles', minzoom: 0, maxzoom: 19 }] },
  satellite: { version: 8, sources: { 'esri-sat': { type: 'raster', tiles: ['https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'], tileSize: 256, attribution: '© Esri World Imagery' } }, layers: [{ id: 'esri-sat-layer', type: 'raster', source: 'esri-sat', minzoom: 0, maxzoom: 19 }] },
  terrain: { version: 8, sources: { 'topo-tiles': { type: 'raster', tiles: ['https://a.tile.opentopomap.org/{z}/{x}/{y}.png'], tileSize: 256, attribution: '© OpenTopoMap' } }, layers: [{ id: 'topo-tiles-layer', type: 'raster', source: 'topo-tiles', minzoom: 0, maxzoom: 17 }] }
};

const transportStyles = {
  car: { color: '#0ea5e9', dasharray: null, width: 6 },
  bike: { color: '#10b981', dasharray: null, width: 4 },
  train: { color: '#ea580c', dasharray: [4, 4], width: 5 }, 
  bus: { color: '#3b82f6', dasharray: null, width: 8 },
  plane: { color: '#a855f7', dasharray: [2, 4], width: 4 }, 
  walking: { color: '#6b7280', dasharray: [1, 2], width: 4 } 
};

const placeholderImages = [
  "https://images.unsplash.com/photo-1548013146-72479768bada?auto=format&fit=crop&w=400&q=80",
  "https://images.unsplash.com/photo-1524492412937-b28074a5d7da?auto=format&fit=crop&w=400&q=80",
  "https://images.unsplash.com/photo-1602216056096-3b40cc0c9944?auto=format&fit=crop&w=400&q=80",
  "https://images.unsplash.com/photo-1590050752117-238cb0fb12b1?auto=format&fit=crop&w=400&q=80",
  "https://images.unsplash.com/photo-1550155416-8316dfa99f18?auto=format&fit=crop&w=400&q=80"
];

// --- HELPER FUNCTIONS ---

const getWeatherBackground = (condition = "") => {
  const lowerCond = condition.toLowerCase();
  if (lowerCond.includes('rain') || lowerCond.includes('drizzle') || lowerCond.includes('shower')) return 'url(https://images.unsplash.com/photo-1515694346937-94d85e41e6f0?auto=format&fit=crop&w=400&q=80)';
  if (lowerCond.includes('thunder') || lowerCond.includes('storm')) return 'url(https://images.unsplash.com/photo-1605727216801-e27ce1d0ce5c?auto=format&fit=crop&w=400&q=80)';
  if (lowerCond.includes('snow') || lowerCond.includes('ice') || lowerCond.includes('blizzard')) return 'url(https://images.unsplash.com/photo-1517299321609-52687d1bc9e2?auto=format&fit=crop&w=400&q=80)';
  if (lowerCond.includes('fog') || lowerCond.includes('mist')) return 'url(https://images.unsplash.com/photo-1485236715568-ddc5ee6ca227?auto=format&fit=crop&w=400&q=80)';
  if (lowerCond.includes('cloud') || lowerCond.includes('overcast')) return 'url(https://images.unsplash.com/photo-1534088568595-a066f410cbda?auto=format&fit=crop&w=400&q=80)';
  if (lowerCond.includes('sun') || lowerCond.includes('clear')) return 'url(https://images.unsplash.com/photo-1622396481328-9b1b78cdd9fd?auto=format&fit=crop&w=400&q=80)';
  return 'url(https://images.unsplash.com/photo-1517685352821-92cf88aee5a5?auto=format&fit=crop&w=400&q=80)'; 
};

const getAQIDetails = (index) => {
  switch(index) {
    case 1: return { text: 'Good', color: '#4ade80' }; 
    case 2: return { text: 'Moderate', color: '#facc15' }; 
    case 3: return { text: 'Unhealthy (Sens.)', color: '#fb923c' }; 
    case 4: return { text: 'Unhealthy', color: '#f87171' }; 
    case 5: return { text: 'Very Unhealthy', color: '#a855f7' }; 
    case 6: return { text: 'Hazardous', color: '#9f1239' }; 
    default: return { text: 'N/A', color: '#9ca3af' }; 
  }
};

const getIconForCategory = (cat) => {
  switch(cat) {
    case "Odessey's Pick": return Sparkles;
    case 'Stays': return Bed;
    case 'Food': return Utensils;
    case 'Amenities': return Fuel;
    case 'Monuments': return Landmark;
    case 'Cities': return Building2;
    case 'Hidden Gems': return Compass;
    default: return Camera;
  }
};

const guessAICategory = (title, desc) => {
  const text = (title + " " + desc).toLowerCase();
  if (text.match(/hotel|resort|stay|lodge/)) return 'Stays';
  if (text.match(/restaurant|cafe|dhaba|eatery/)) return 'Food';
  if (text.match(/fort|temple|palace|monument|tomb|heritage|history/)) return 'Monuments';
  if (text.match(/city|town|capital/)) return 'Cities';
  return 'Hidden Gems'; 
};

// --- COMPONENTS ---

const SearchAutocomplete = ({ value, onChange, placeholder, Icon, iconColor, disabled, inputStyle }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (!value || value.trim().length < 2) {
      setSuggestions([]);
      return;
    }

    const delayDebounceFn = setTimeout(async () => {
      try {
        const response = await fetch(`/api/get_ola_search`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ query: value }),
        });

        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();

        if (data?.results) {
          setSuggestions(data.results.map((item) => item.formatted_address));
        } else {
          setSuggestions([]);
        }
      } catch (e) {
        console.warn("Search API Error:", e);
        setSuggestions([]);
      }
    }, 400);

    return () => clearTimeout(delayDebounceFn);
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (e) => { if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setShowDropdown(false); };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="route-autocomplete-wrapper" style={{ flex: '1 1 220px', position: 'relative', width: '100%', boxSizing: 'border-box' }}>
      {Icon && <Icon size={20} color={iconColor || "#9ca3af"} style={{ position: 'absolute', left: '15px', top: '50%', transform: 'translateY(-50%)', zIndex: 10 }} />}
      <input type="text" placeholder={placeholder} value={value} onChange={(e) => { onChange(e.target.value); setShowDropdown(true); }} onFocus={() => { if(suggestions.length > 0) setShowDropdown(true); }} disabled={disabled} style={{ width: '100%', boxSizing: 'border-box', padding: '16px 16px 16px 45px', borderRadius: '12px', outline: 'none', fontSize: '15px', fontWeight: 'bold', ...inputStyle }} />
      {showDropdown && suggestions.length > 0 && (
        <ul style={{ position: 'absolute', top: '100%', left: 0, right: 0, backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 10px 25px rgba(0,0,0,0.2)', listStyle: 'none', margin: '8px 0 0 0', padding: '5px 0', zIndex: 50, maxHeight: '220px', overflowY: 'auto', border: '1px solid #e5e7eb' }}>
          {suggestions.map((s, i) => (
            <li key={i} onClick={() => { onChange(s); setShowDropdown(false); }} style={{ padding: '12px 15px', cursor: 'pointer', borderBottom: i === suggestions.length - 1 ? 'none' : '1px solid #f3f4f6', fontSize: '14px', color: '#374151', transition: 'background 0.2s', display: 'flex', alignItems: 'center', gap: '8px' }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0fdf4'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}>
              <MapPin size={16} color="#16a34a" style={{ flexShrink: 0 }} /> <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const RoutePlannerPage = () => {
  const navigate = useNavigate();
  const { isDark } = useTheme();

  const [origin, setOrigin] = useMemory('odessey_rp_origin', '');
  const [destination, setDestination] = useMemory('odessey_rp_dest', '');
  const [isSearching, setIsSearching] = useMemory('odessey_rp_search', false);
  const [itineraryStops, setItineraryStops] = useMemory('odessey_rp_stops', []);
  const [routeCoords, setRouteCoords] = useMemory('odessey_rp_coords', { start: null, end: null });

  const [globalTransportMode, setGlobalTransportMode] = useState('car');
  const [legModes, setLegModes] = useState({});

  const [isLoading, setIsLoading] = useState(false);
  const [isSuggesting, setIsSuggesting] = useState(false); 
  const [loadingText, setLoadingText] = useState('');
  
  const [suggestedPlaces, setSuggestedPlaces] = useState([]);
  
  const [routeOptions, setRouteOptions] = useState([]);
  const [selectedRouteIndex, setSelectedRouteIndex] = useState(0);
  const [routeLegs, setRouteLegs] = useState([]);
  
  const [customStopName, setCustomStopName] = useState('');
  const [isFindingLocation, setIsFindingLocation] = useState(false);

  const [weatherData, setWeatherData] = useState([]);
  const [weatherAlerts, setWeatherAlerts] = useState([]);
  const [isWeatherLoading, setIsWeatherLoading] = useState(false);

  const [activeCategory, setActiveCategory] = useState('All');
  const [recPage, setRecPage] = useState(0);
  const ITEMS_PER_PAGE = 4;
  const categories = ['All', "Odessey's Pick", 'Cities', 'Monuments', 'Hidden Gems', 'Stays', 'Food', 'Amenities'];

  const [currentMapStyle, setCurrentMapStyle] = useState('street');
  const [mapRenderTrigger, setMapRenderTrigger] = useState(0); 

  const mapContainerRef = useRef(null);
  const mapInstance = useRef(null);
  const markersRef = useRef([]); 
  const dragItem = useRef();
  const dragOverItem = useRef();

  const addStop = async (stop) => {
    if (!itineraryStops.find(s => s.id === stop.id)) {
      try {
        const descRes = await fetch(`/api/get_description?query=${encodeURIComponent(stop.name)}`);
        if (descRes.ok) {
          const descData = await descRes.json();
          if (descData.description) {
            stop = { ...stop, desc: descData.description };
          }
        }
      } catch (err) {}
      setItineraryStops(prev => [...prev, stop]);
    }
  };

  const removeStop = (id) => setItineraryStops(itineraryStops.filter(s => s.id !== id));

  const handleReset = () => {
    setOrigin(''); setDestination(''); setIsSearching(false); setItineraryStops([]); setSuggestedPlaces([]);
    setRouteCoords({ start: null, end: null }); setRouteOptions([]); setRouteLegs([]); setWeatherData([]);
    setWeatherAlerts([]); setCustomStopName(''); setActiveCategory('All'); setRecPage(0); setLegModes({});
    if (mapInstance.current) { mapInstance.current.remove(); mapInstance.current = null; }
  };

  const getCoordinates = async (placeName) => {
    try {
      let lat = null, lng = null, name = placeName, defaultAddress = "";
      try {
        const resolveRes = await fetch(`/api/resolve_location?query=${encodeURIComponent(placeName)}`);
        if (resolveRes.ok) {
          const resolveData = await resolveRes.json();
          if (resolveData.is_zip_code && resolveData.location && resolveData.location.lat) {
            lat = parseFloat(resolveData.location.lat);
            lng = parseFloat(resolveData.location.lon || resolveData.location.lng);
            name = resolveData.location.display_name || placeName;
            defaultAddress = resolveData.location.display_name || "";
          }
        }
      } catch (err) {}

      if (!lat || !lng) {
        try {
          const response = await fetch(`/api/geocode`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ query: placeName }),
          });
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          const result = await response.json();
          if (result) {
            lat = result.lat;
            lng = result.lng;
            name = result.formatted_address || placeName;
            defaultAddress = result.formatted_address || "";
          }
        } catch (e) {}
      }

      if (!lat || !lng) return null;

      let finalDesc = defaultAddress || "Coordinates acquired.";
      try {
        const descRes = await fetch(`/api/get_description?query=${encodeURIComponent(name)}`);
        if (descRes.ok) {
          const descData = await descRes.json();
          if (descData.description) finalDesc = descData.description;
        }
      } catch (err) {}
      return { lat, lng, name, desc: finalDesc };
    } catch (e) {}
    return null;
  };

  const handleSearchRoutes = async (e) => {
    e.preventDefault();
    if (!origin || !destination) return;
    setIsLoading(true); setIsSearching(false); setItineraryStops([]); setActiveCategory('All'); setRecPage(0); setLegModes({});

    try {
      setLoadingText('Locating Origin...'); const startLoc = await getCoordinates(origin);
      setLoadingText('Locating Destination...'); const endLoc = await getCoordinates(destination);

      if (!startLoc || !endLoc) { alert("Could not find exact coordinates for those cities."); setIsLoading(false); return; }
      setRouteCoords({ start: startLoc, end: endLoc });
      setIsSearching(true);
    } catch (error) { alert("Error generating route."); } finally { setIsLoading(false); }
  };

  // ✅ SAFELY HOISTED AND MEMOIZED MAP RENDER FUNCTION
  const updateMapEntities = useCallback(() => {
    const map = mapInstance.current;
    if (!map || !map.isStyleLoaded() || !routeCoords.start || !routeCoords.end || !window.maplibregl) return;

    markersRef.current.forEach(m => m.remove());
    markersRef.current = [];

    const createMarkerEl = (name, color) => {
      const el = document.createElement('div');
      el.innerHTML = `
        <div style="position: relative; display: flex; align-items: center; justify-content: center; transform: translate(-50%, -50%); cursor: pointer;">
          <div style="width: 16px; height: 16px; background-color: ${color}; border-radius: 50%; border: 3px solid white; box-shadow: 0 3px 8px rgba(0,0,0,0.4); z-index: 2;"></div>
          <div style="position: absolute; left: 22px; white-space: nowrap; font-family: system-ui; font-size: 13px; font-weight: 800; color: ${currentMapStyle === 'satellite' ? 'white' : '#111827'}; text-shadow: ${currentMapStyle === 'satellite' ? '1px 1px 4px black' : '1px 1px 2px white, -1px -1px 2px white, 1px -1px 2px white, -1px 1px 2px white'}; z-index: 1;">
            ${name}
          </div>
        </div>`;
      return el;
    };

    markersRef.current.push(new window.maplibregl.Marker({ element: createMarkerEl(origin.toUpperCase(), '#111827') }).setLngLat([routeCoords.start.lng, routeCoords.start.lat]).addTo(map));
    itineraryStops.forEach(stop => markersRef.current.push(new window.maplibregl.Marker({ element: createMarkerEl(stop.name, '#16a34a') }).setLngLat([stop.lng, stop.lat]).addTo(map)));
    markersRef.current.push(new window.maplibregl.Marker({ element: createMarkerEl(destination.toUpperCase(), '#ea580c') }).setLngLat([routeCoords.end.lng, routeCoords.end.lat]).addTo(map));

    if (routeOptions.length > 0) {
      const legFeatures = [];

      routeOptions.forEach((route, routeIdx) => {
        const isSelected = routeIdx === selectedRouteIndex;
        
        if (!isSelected) {
           legFeatures.push({
             type: 'Feature',
             properties: { routeIndex: routeIdx, isSelected: false, mode: 'alt' },
             geometry: route.geometry
           });
           return;
        }

        const fullCoords = route.geometry.coordinates;
        const currentLegs = route.legs || routeLegs;
        
        if (currentLegs && currentLegs.length > 0) {
          let coordOffset = 0;
          currentLegs.forEach((leg, legIdx) => {
            const mode = legModes[legIdx] || globalTransportMode;
            const legDistanceRatio = leg.distance / Math.max(1, route.distance);
            let segmentSize = Math.floor(fullCoords.length * legDistanceRatio);
            
            if (legIdx === currentLegs.length - 1) segmentSize = fullCoords.length - coordOffset; 

            const startPt = coordOffset;
            const endPt = Math.min(fullCoords.length, coordOffset + segmentSize + 1);

            const legCoords = fullCoords.slice(startPt, endPt);
            coordOffset += segmentSize;

            if (legCoords.length >= 2) {
              legFeatures.push({
                type: 'Feature',
                properties: { legIndex: legIdx, mode: mode, isSelected: true, routeIndex: routeIdx },
                geometry: { type: 'LineString', coordinates: legCoords }
              });
            }
          });
        } else {
          // Fallback if legs array fails parsing
          legFeatures.push({
            type: 'Feature',
            properties: { routeIndex: routeIdx, mode: globalTransportMode, isSelected: true },
            geometry: route.geometry
          });
        }
      });

      // Safely declare fallback main feature just in case legFeatures is completely empty
      const mainRouteFeature = {
        type: 'Feature',
        properties: { routeIndex: 0, mode: globalTransportMode, isSelected: true },
        geometry: routeOptions[selectedRouteIndex].geometry
      };

      const geojson = { type: 'FeatureCollection', features: legFeatures.length > 0 ? legFeatures : [mainRouteFeature] };

      try {
        if (!map.getSource('routes-source')) {
          map.addSource('routes-source', { type: 'geojson', data: geojson });
        } else {
          map.getSource('routes-source').setData(geojson);
        }
          
        if (!map.getLayer('route-lines-alt')) {
          map.addLayer({
            id: 'route-lines-alt',
            type: 'line',
            source: 'routes-source',
            filter: ['==', 'isSelected', false],
            layout: { 'line-join': 'round', 'line-cap': 'round' },
            paint: { 'line-color': '#9ca3af', 'line-width': 4, 'line-opacity': 0.5 }
          });
        }

        Object.keys(transportStyles).forEach(mode => {
          const layerId = `route-lines-${mode}`;
          if (!map.getLayer(layerId)) {
            const paintObj = {
              'line-color': transportStyles[mode].color,
              'line-width': transportStyles[mode].width,
              'line-opacity': 0.85
            };
            if (transportStyles[mode].dasharray) paintObj['line-dasharray'] = transportStyles[mode].dasharray;

            map.addLayer({
              id: layerId,
              type: 'line',
              source: 'routes-source',
              filter: ['all', ['==', 'isSelected', true], ['==', 'mode', mode]],
              layout: { 'line-join': 'round', 'line-cap': 'round' },
              paint: paintObj
            });
          }
        });

        const clickableLayers = Object.keys(transportStyles).map(m => `route-lines-${m}`).concat(['route-lines-alt']);
        if (!map.hasRouteClickListener) {
          clickableLayers.forEach(layerId => {
            map.on('click', layerId, (e) => {
              if (e.features.length > 0) {
                const clickedIdx = e.features[0].properties.routeIndex;
                if (clickedIdx !== undefined && clickedIdx !== selectedRouteIndex) setSelectedRouteIndex(clickedIdx);
              }
            });
            map.on('mouseenter', layerId, () => map.getCanvas().style.cursor = 'pointer');
            map.on('mouseleave', layerId, () => map.getCanvas().style.cursor = '');
          });
          map.hasRouteClickListener = true;
        }
      } catch (err) {
        console.warn("Map layer sync interrupted during transition.", err);
      }

      const bounds = new window.maplibregl.LngLatBounds();
      routeOptions[selectedRouteIndex].geometry.coordinates.forEach(coord => bounds.extend(coord));
      map.fitBounds(bounds, { padding: 80, maxZoom: 12 });
    }
  }, [routeOptions, selectedRouteIndex, currentMapStyle, origin, destination, itineraryStops, routeCoords, globalTransportMode, legModes, routeLegs]);

  // INITIALIZE MAP
  useEffect(() => {
    if (!isSearching || !mapContainerRef.current) return;

    const initMap = () => {
      if (mapInstance.current) return; 
      const map = new window.maplibregl.Map({
        container: mapContainerRef.current, 
        style: JSON.parse(JSON.stringify(mapLayouts[currentMapStyle])),
        center: [routeCoords.start.lng, routeCoords.start.lat],
        zoom: 5, attributionControl: false
      });
      map.addControl(new window.maplibregl.NavigationControl({ visualizePitch: true }), 'top-right');
      map.on('load', () => { mapInstance.current = map; setMapRenderTrigger(Date.now()); });
    };

    if (!window.maplibregl) {
      if (!document.getElementById('maplibre-css')) {
        const link = document.createElement('link'); link.id = 'maplibre-css'; link.href = 'https://unpkg.com/maplibre-gl@3.x/dist/maplibre-gl.css'; link.rel = 'stylesheet'; document.head.appendChild(link);
      }
      if (!document.getElementById('maplibre-js')) {
        const script = document.createElement('script'); script.id = 'maplibre-js'; script.src = 'https://unpkg.com/maplibre-gl@3.x/dist/maplibre-gl.js'; script.async = true; script.onload = initMap; document.head.appendChild(script);
      } else {
        document.getElementById('maplibre-js').addEventListener('load', initMap);
      }
    } else { initMap(); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSearching, routeCoords]); // Deliberately omit currentMapStyle to prevent loop

  // ✅ SAFELY HANDLE MAP STYLE TOGGLES (Re-render routes exclusively when MapLibre is IDLE)
  useEffect(() => {
    if (mapInstance.current && window.maplibregl) {
      mapInstance.current.setStyle(JSON.parse(JSON.stringify(mapLayouts[currentMapStyle])));
      
      // Wait for the 'idle' event: guarantees the new style is completely loaded, parsed, and painted.
      mapInstance.current.once('idle', () => {
        setMapRenderTrigger(Date.now());
      });
    }
  }, [currentMapStyle]);

  // APPLY MARKERS & LINES ON RENDER TRIGGER
  useEffect(() => {
    const map = mapInstance.current;
    if (map) {
      if (map.isStyleLoaded()) { updateMapEntities(); }
      else { map.once('idle', updateMapEntities); }
    }
  }, [updateMapEntities, mapRenderTrigger]);

  // Recalculate route metrics based on individual leg preference modes
  useEffect(() => {
    if (!routeCoords.start || !routeCoords.end) return;
    const fetchRoutes = async () => {
      const waypoints = [routeCoords.start, ...itineraryStops, routeCoords.end];
      const coordsString = waypoints.map(wp => `${wp.lng},${wp.lat}`).join(';');
      try {
        const res = await fetch(`https://router.project-osrm.org/route/v1/driving/${coordsString}?overview=full&geometries=geojson&alternatives=3`);
        const data = await res.json();
        if (data.routes && data.routes.length > 0) {
          const adjustedRoutes = data.routes.map(route => {
            const updatedLegs = route.legs.map((leg, legIdx) => {
              const activeLegMode = legModes[legIdx] || globalTransportMode;
              let speedMultiplier = 1.0;
              if (activeLegMode === 'train') speedMultiplier = 0.85;
              else if (activeLegMode === 'bus') speedMultiplier = 1.25;
              else if (activeLegMode === 'bike') speedMultiplier = 1.15;
              else if (activeLegMode === 'plane') speedMultiplier = 0.25;
              else if (activeLegMode === 'walking') speedMultiplier = 15.0;

              return {
                ...leg,
                mode: activeLegMode,
                duration: Math.round(leg.duration * speedMultiplier)
              };
            });

            const totalDuration = updatedLegs.reduce((sum, l) => sum + l.duration, 0);

            return {
              ...route,
              duration: totalDuration,
              legs: updatedLegs
            };
          });

          setRouteOptions(adjustedRoutes);
          setSelectedRouteIndex(0);
          setRouteLegs(adjustedRoutes[0].legs || []);
        }
      } catch (error) { console.error("Error fetching OSRM routes:", error); }
    };
    fetchRoutes();
  }, [routeCoords.start?.lat, routeCoords.start?.lng, routeCoords.end?.lat, routeCoords.end?.lng, itineraryStops, globalTransportMode, legModes]);

  useEffect(() => {
    if (routeOptions.length === 0 || !routeOptions[selectedRouteIndex]) return;

    const fetchDynamicSuggestions = async () => {
      setIsSuggesting(true);
      try {
        const currentRoute = routeOptions[selectedRouteIndex];
        const coords = currentRoute.geometry.coordinates;

        const searchCoords = [];
        const numSamples = 4; 
        const step = Math.max(1, Math.floor(coords.length / numSamples));
        for (let i = 0; i < coords.length; i += step) searchCoords.push({ lat: coords[i][1], lng: coords[i][0] });
        searchCoords.push({ lat: coords[coords.length - 1][1], lng: coords[coords.length - 1][0] });

        const queries = [
          { term: 'hotel', defaultCat: 'Stays' },
          { term: 'resort', defaultCat: 'Stays' },
          { term: 'restaurant', defaultCat: 'Food' },
          { term: 'cafe', defaultCat: 'Food' },
          { term: 'hospital', defaultCat: 'Amenities' },
          { term: 'fuel pump', defaultCat: 'Amenities' },
          { term: 'historical monument', defaultCat: 'Monuments' },
          { term: 'tourist viewpoint', defaultCat: 'Hidden Gems' },
          { term: 'town', defaultCat: 'Cities' }
        ];

        const fetchPromises = [];
        searchCoords.forEach(() => {
          queries.forEach((q) => {
            fetchPromises.push(
              fetch(`/api/get_ola_search`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ query: q.term }),
              })
                .then((r) => {
                  if (!r.ok) throw new Error(`HTTP ${r.status}`);
                  return r.json();
                })
                .then((data) => {
                  const items = data.results || [];
                  return items.map((p) => ({
                    feature: {
                      ...p,
                      geometry: { location: { lat: p.lat, lng: p.lng } },
                    },
                    forcedCat: q.defaultCat,
                  }));
                })
                .catch((err) => { return []; })
            );
          });
        });

        const allResults = await Promise.all(fetchPromises);
        const flatResults = allResults.flat();
        
        const uniquePlaces = new Map();
        flatResults.forEach(item => {
          const f = item.feature;
          const rawName = f.name || f.structured_formatting?.main_text || (f.description ? f.description.split(',')[0] : '');
          if (!rawName) return; 
          
          const cleanName = rawName.toLowerCase().trim();
          const genericWords = ['hotel', 'resort', 'restaurant', 'cafe', 'hospital', 'clinic', 'fuel', 'petrol pump', 'temple', 'monument', 'fort', 'waterfall', 'viewpoint', 'attraction', 'town', 'city', 'village', 'atm', 'bank', 'parking', 'dhaba'];
          if (genericWords.includes(cleanName) || cleanName.length <= 3) return;

          const cat = item.forcedCat; 
          const id = f.place_id || f.reference || rawName;
          
          if (!uniquePlaces.has(id) && !itineraryStops.some(s => s.name.toLowerCase() === rawName.toLowerCase())) {
            const lat = f.geometry?.location?.lat || f.location?.lat;
            const lng = f.geometry?.location?.lng || f.location?.lng;
            if (!lat || !lng) return;

            uniquePlaces.set(id, {
              id: id, name: rawName, lat: lat, lng: lng, type: cat, category: cat, isOdesseyPick: false, time: "Route Stop",
              icon: getIconForCategory(cat), desc: f.formatted_address || f.description || `A notable ${cat.toLowerCase()} directly on your route.`,
              img: placeholderImages[uniquePlaces.size % placeholderImages.length]
            });
          }
        });

        try {
          if (origin && destination) {
            const getCleanCityName = (address) => {
              if (!address) return "";
              const parts = address.split(',').map(p => p.trim());
              const stopWords = ['india', 'ind', 'in', 'gujarat', 'delhi', 'maharashtra', 'rajasthan', 'haryana', 'uttar pradesh', 'madhya pradesh', 'punjab', 'karnataka', 'goa', 'telangana', 'west bengal'];
              const cleanParts = parts.filter(p => !stopWords.includes(p.toLowerCase()));
              if (cleanParts.length > 0) return cleanParts[cleanParts.length - 1]; 
              return parts[0]; 
            };
            const cleanOrigin = getCleanCityName(origin);
            const cleanDest = getCleanCityName(destination);
            
            const searchQuery = `You are a road trip planning expert. A traveler is journeying from ${cleanOrigin} to ${cleanDest}. Return exactly 5 unique, real points of interest located directly along the route as JSON.
{ "route": "${cleanOrigin} to ${cleanDest}", "cities": [{ "name": "City Name", "why_stop": "One line reason" }], "historical_monuments": [{ "name": "Monument Name", "city": "Nearest city", "why_stop": "One line reason" }], "nature_spots": [{ "name": "Spot Name", "city": "Nearest city", "why_stop": "One line reason" }], "hotels": [{ "name": "Hotel Name", "city": "City it's in", "star_rating": 3 }], "restaurants": [{ "name": "Restaurant Name", "city": "City it's in", "cuisine": "Cuisine type" }] }`;

            const aiRes = await fetch('/api/chat', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ messages: [{ role: 'user', content: searchQuery }], max_tokens: 2048 }) });

            if (aiRes.ok) {
              const chatData = await aiRes.json();
              const rawText = chatData.reply || '';
              let routeData = null;
              try {
                const startIdx = rawText.indexOf('{');
                const endIdx = rawText.lastIndexOf('}');
                if (startIdx !== -1 && endIdx !== -1) routeData = JSON.parse(rawText.substring(startIdx, endIdx + 1));
              } catch {}
              const allRecs = routeData ? [
                ...(routeData.cities || []).map(c => ({ title: c.name, description: c.why_stop || '' })),
                ...(routeData.historical_monuments || []).map(m => ({ title: m.name, description: m.why_stop || '' })),
                ...(routeData.nature_spots || []).map(n => ({ title: n.name, description: n.why_stop || '' })),
                ...(routeData.hotels || []).map(h => ({ title: h.name, description: h.city ? `Hotel in ${h.city}` : '' })),
                ...(routeData.restaurants || []).map(r => ({ title: r.name, description: r.cuisine ? `${r.cuisine} in ${r.city || ''}` : '' })),
              ] : [];
              if (allRecs.length > 0) {
                for (const rec of allRecs) {
                  const finalTitle = rec.title || "Unnamed Stop";
                  const finalDesc = rec.description || "";
                  if (!itineraryStops.some(s => s.name.toLowerCase() === finalTitle.toLowerCase())) {
                    const locCoords = await getCoordinates(`${finalTitle}, India`);
                    if (locCoords) {
                      const id = `ai_${Date.now()}_${Math.random()}`;
                      const predictedCat = guessAICategory(finalTitle, finalDesc);
                      uniquePlaces.set(id, {
                        id: id, name: finalTitle, lat: locCoords.lat, lng: locCoords.lng, type: predictedCat, category: predictedCat, isOdesseyPick: true, time: "Route Stop", icon: getIconForCategory(predictedCat), desc: finalDesc, img: placeholderImages[uniquePlaces.size % placeholderImages.length]
                      });
                    }
                  }
                }
              }
            }
          }
        } catch (err) {}

        const dynamicPlaces = Array.from(uniquePlaces.values());
        dynamicPlaces.sort((a, b) => {
          if (a.isOdesseyPick && !b.isOdesseyPick) return -1;
          if (!a.isOdesseyPick && b.isOdesseyPick) return 1;
          const rank = { 'Cities': 1, 'Monuments': 2, 'Hidden Gems': 3, 'Food': 4, 'Stays': 5, 'Amenities': 6 };
          return (rank[a.category] || 99) - (rank[b.category] || 99);
        });

        setSuggestedPlaces(dynamicPlaces);
        setRecPage(0); 
      } catch (error) {} finally { setIsSuggesting(false); }
    };
    
    const debounceTimer = setTimeout(() => { fetchDynamicSuggestions(); }, 800);
    return () => clearTimeout(debounceTimer);
  }, [routeOptions, selectedRouteIndex, itineraryStops, origin, destination]);

  useEffect(() => {
    if (!routeCoords.start || !routeCoords.end) return;
    const fetchWeather = async () => {
      setIsWeatherLoading(true);
      const pointsToFetch = [ { ...routeCoords.start, type: "Start", displayName: origin }, ...itineraryStops.map((stop, i) => ({ ...stop, type: `Stop ${i + 1}`, displayName: stop.name, })), { ...routeCoords.end, type: "End", displayName: destination } ];

      try {
        const weatherPromises = pointsToFetch.map(async (pt) => {
          const response = await fetch(`/api/weather`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ lat: pt.lat, lng: pt.lng }) });
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          const data = await response.json();
          return { ptType: pt.type, name: pt.displayName, temp: data.temp, condition: data.condition || "Clear", icon: data.icon, aqiIndex: data.aqiIndex, alerts: data.alerts || [] };
        });

        const results = await Promise.all(weatherPromises);
        setWeatherData(results);

        const allAlerts = [];
        results.forEach((res) => {
          if (res.alerts?.length) {
            res.alerts.forEach((alert) => {
              if (!allAlerts.find((a) => a.event === alert.event && a.location === res.name)) {
                allAlerts.push({ location: res.name, ...alert });
              }
            });
          }
        });
        setWeatherAlerts(allAlerts);
      } catch (error) {} finally { setIsWeatherLoading(false); }
    };

    const debounceWeather = setTimeout(() => { fetchWeather(); }, 800);
    return () => clearTimeout(debounceWeather);
  }, [routeCoords.start?.lat, routeCoords.start?.lng, routeCoords.end?.lat, routeCoords.end?.lng, itineraryStops, origin, destination]);

  const handleAddCustomStop = async (e) => {
    e.preventDefault();
    if (!customStopName.trim()) return;

    setIsFindingLocation(true);
    try {
      const locationData = await getCoordinates(customStopName);
      if (locationData) {
        const customStop = {
          id: Date.now(), name: locationData.name, lat: locationData.lat, lng: locationData.lng,     
          type: "Custom Stop", time: "Flexible", icon: MapPin,
          desc: locationData.desc || "A personalized location added by you.", img: placeholderImages[0]
        };
        setItineraryStops([...itineraryStops, customStop]);
        setCustomStopName(''); 
      } else { alert(`Could not find "${customStopName}". Try adding a city name (e.g. "Juhu Beach, Mumbai").`); }
    } catch (error) {} finally { setIsFindingLocation(false); }
  };

  const handleDragStart = (e, position) => { dragItem.current = position; e.target.style.opacity = 0.5; };
  const handleDragEnter = (e, position) => { e.preventDefault(); dragOverItem.current = position; };
  const handleDrop = (e) => {
    e.preventDefault();
    const copyListItems = [...itineraryStops];
    const dragItemContent = copyListItems[dragItem.current];
    copyListItems.splice(dragItem.current, 1);
    copyListItems.splice(dragOverItem.current, 0, dragItemContent);
    setItineraryStops(copyListItems);
    dragItem.current = null; dragOverItem.current = null; e.target.style.opacity = 1;
  };
  const handleDragEnd = (e) => { e.target.style.opacity = 1; };

  const handleFinalize = () => { 
    const stopsList = itineraryStops.map(stop => stop.name).join(', ');
    const interestsString = stopsList.length > 0 ? `Please ensure my route stops at the following locations: ${stopsList}.` : `Planning a direct journey from ${origin} to ${destination}.`;
    const cleanStops = itineraryStops.map(stop => ({ id: stop.id, name: stop.name, lat: stop.lat, lng: stop.lng, type: stop.type, desc: stop.desc }));
    navigate('/plan', { state: { origin: origin, destination: destination, interests: interestsString, transportMode: globalTransportMode, routeStops: cleanStops, legModes: legModes, isConsumed: false } }); 
  };

  const totalDistMeters = routeLegs.reduce((sum, leg) => sum + leg.distance, 0);
  const totalDurSeconds = routeLegs.reduce((sum, leg) => sum + leg.duration, 0);
  const totalDistKm = (totalDistMeters / 1000).toFixed(1);
  const totalHrs = Math.floor(totalDurSeconds / 3600);
  const totalMins = Math.floor((totalDurSeconds % 3600) / 60);
  const totalTimeStr = totalHrs > 0 ? `${totalHrs}h ${totalMins}m` : `${totalMins}m`;

  const handleLegModeChange = (legIndex, newMode) => {
    setLegModes(prev => ({ ...prev, [legIndex]: newMode }));
  };

  const renderLegInfo = (index) => {
    if (!routeLegs[index]) return null;
    const leg = routeLegs[index];
    const dist = (leg.distance / 1000).toFixed(1);
    const hrs = Math.floor(leg.duration / 3600);
    const mins = Math.floor((leg.duration % 3600) / 60);
    const time = hrs > 0 ? `${hrs}h ${mins}m` : `${mins}m`;
    const activeMode = legModes[index] || globalTransportMode;

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', padding: '8px 0' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <div style={{ backgroundColor: isDark ? '#111827' : 'white', padding: '6px 12px', borderRadius: '50px', fontSize: '11px', fontWeight: 'bold', color: isDark ? '#d1d5db' : '#4b5563', display: 'flex', alignItems: 'center', gap: '5px', border: isDark ? '1px solid #374151' : '1px solid #e5e7eb', boxShadow: '0 2px 5px rgba(0,0,0,0.02)' }}>
            <Navigation size={12} color={transportStyles[activeMode]?.color || "#0284c7"} /> {dist} km
          </div>
          <div style={{ backgroundColor: isDark ? '#111827' : 'white', padding: '6px 12px', borderRadius: '50px', fontSize: '11px', fontWeight: 'bold', color: isDark ? '#d1d5db' : '#4b5563', display: 'flex', alignItems: 'center', gap: '5px', border: isDark ? '1px solid #374151' : '1px solid #e5e7eb', boxShadow: '0 2px 5px rgba(0,0,0,0.02)' }}>
            <Clock size={12} color="#ea580c" /> {time}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ fontSize: '11px', fontWeight: 'bold', color: isDark ? '#9ca3af' : '#6b7280' }}>Leg Mode:</span>
          <select value={activeMode} onChange={(e) => handleLegModeChange(index, e.target.value)} style={{ padding: '4px 8px', borderRadius: '8px', fontSize: '11px', fontWeight: 'bold', outline: 'none', cursor: 'pointer', backgroundColor: isDark ? '#111827' : '#f3f4f6', color: transportStyles[activeMode]?.color || (isDark ? '#38bdf8' : '#0284c7'), border: isDark ? '1px solid #4b5563' : '1px solid #d1d5db' }}>
            <option value="car">🚗 Car</option>
            <option value="bike">🏍️ Two-Wheeler / Bike</option>
            <option value="train">🚆 Train</option>
            <option value="bus">🚌 Bus</option>
            <option value="plane">✈️ Plane</option>
            <option value="walking">🚶 Walking</option>
          </select>
        </div>
      </div>
    );
  };

  const filteredPlaces = activeCategory === 'All' ? suggestedPlaces : activeCategory === "Odessey's Pick" ? suggestedPlaces.filter(p => p.isOdesseyPick) : suggestedPlaces.filter(p => p.category === activeCategory);
  const totalPages = Math.ceil(filteredPlaces.length / ITEMS_PER_PAGE);
  const currentDisplayedRecs = filteredPlaces.slice(recPage * ITEMS_PER_PAGE, (recPage + 1) * ITEMS_PER_PAGE);

  return (
    <div style={{ minHeight: '100vh', backgroundColor: isDark ? '#111827' : '#f9fafb', fontFamily: 'system-ui, -apple-system, sans-serif', paddingBottom: '80px', overflowX: 'hidden', transition: 'background-color 0.3s' }}>
      
      {/* 1. HERO SECTION WITH GLOBAL VEHICLE PREFERENCE BAR INCLUDING TWO-WHEELER */}
      <div className="route-hero-container" style={{ position: 'relative', zIndex: 10, height: isSearching ? '300px' : '480px', transition: 'all 0.4s ease-in-out', backgroundImage: 'url(https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?auto=format&fit=crop&w=1920&q=80)', backgroundSize: 'cover', backgroundPosition: 'center' }}>
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, rgba(17, 24, 39, 0.75), rgba(17, 24, 39, 0.95))' }} />
        <div className="route-hero-content" style={{ position: 'absolute', top: isSearching ? '80px' : '50%', left: '50%', transform: isSearching ? 'translateX(-50%)' : 'translate(-50%, -50%)', width: '100%', maxWidth: '1000px', padding: '0 20px', boxSizing: 'border-box', transition: 'all 0.4s ease-in-out' }}>
          
          <h1 style={{ fontSize: isSearching ? '26px' : '44px', fontWeight: '800', color: 'white', marginBottom: '8px', textAlign: 'center', transition: 'all 0.3s' }}>
            Interactive Route Planner
          </h1>
          <p style={{ fontSize: isSearching ? '13px' : '16px', color: 'rgba(255,255,255,0.9)', textAlign: 'center', marginBottom: isSearching ? '16px' : '30px', transition: 'all 0.3s' }}>
            Choose your default transport preference and map your custom journey across India.
          </p>

          <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
            {[ { id: 'car', label: 'Car', icon: Car }, { id: 'bike', label: 'Two-Wheeler', icon: Bike }, { id: 'train', label: 'Train', icon: Train }, { id: 'bus', label: 'Bus', icon: Bus }, { id: 'plane', label: 'Plane', icon: Plane }, { id: 'walking', label: 'Walking', icon: Footprints } ].map((mode) => {
              const IconComponent = mode.icon;
              const isSelected = globalTransportMode === mode.id;
              return (
                <button key={mode.id} type="button" onClick={() => setGlobalTransportMode(mode.id)} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px', borderRadius: '50px', fontSize: '13px', fontWeight: 'bold', cursor: 'pointer', transition: 'all 0.2s', border: 'none', backgroundColor: isSelected ? '#16a34a' : 'rgba(255, 255, 255, 0.15)', color: 'white', backdropFilter: 'blur(5px)', boxShadow: isSelected ? '0 4px 12px rgba(22, 163, 74, 0.4)' : 'none' }}>
                  <IconComponent size={15} /> {mode.label}
                </button>
              );
            })}
          </div>

          <form onSubmit={handleSearchRoutes} className="route-search-form" style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', backgroundColor: isDark ? 'rgba(31, 41, 55, 0.7)' : 'rgba(255,255,255,0.1)', backdropFilter: 'blur(12px)', padding: '15px', borderRadius: '20px', border: isDark ? '1px solid #374151' : '1px solid rgba(255,255,255,0.2)' }}>
            <SearchAutocomplete value={origin} onChange={setOrigin} placeholder="Start (e.g. Delhi)" Icon={Navigation} iconColor="#9ca3af" disabled={isLoading} inputStyle={{ border: 'none' }} />
            <div className="route-arrow-icon" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 10px', color: 'white' }}><ArrowRight size={20} /></div>
            <SearchAutocomplete value={destination} onChange={setDestination} placeholder="End (e.g. Ahmedabad)" Icon={MapPin} iconColor="#16a34a" disabled={isLoading} inputStyle={{ border: 'none' }} />
            
            <div className="route-form-buttons" style={{ display: 'flex', gap: '10px', flex: '0 1 auto' }}>
              <button type="submit" disabled={isLoading} style={{ flex: '1', padding: '0 30px', borderRadius: '12px', backgroundColor: '#16a34a', color: 'white', fontWeight: 'bold', fontSize: '16px', border: 'none', cursor: isLoading ? 'not-allowed' : 'pointer', height: '54px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                {isLoading ? <><Loader2 size={18} className="animate-spin" /> {loadingText}</> : 'Map Route'}
              </button>
              {(isSearching || origin || destination) && (
                <button type="button" onClick={handleReset} style={{ flex: '0 0 auto', padding: '0 20px', borderRadius: '12px', backgroundColor: 'transparent', color: 'white', fontWeight: 'bold', fontSize: '16px', border: '1px solid rgba(255,255,255,0.5)', cursor: 'pointer', height: '54px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', transition: 'all 0.2s', backdropFilter: 'blur(5px)' }}>
                  <RotateCcw size={18} /> Reset
                </button>
              )}
            </div>
          </form>
        </div>
      </div>

      {/* 2. THREE-COLUMN DASHBOARD */}
      {isSearching && (
        <div style={{ maxWidth: '1400px', margin: '40px auto', padding: '0 20px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))', gap: '30px', animation: 'fadeIn 0.5s ease-out' }}>
          
          {/* COLUMN 1: TIMELINE WITH PER-STOP PREFERENCES */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', position: 'sticky', top: '100px', height: 'fit-content' }}>
            <div style={{ backgroundColor: isDark ? '#1f2937' : 'white', borderRadius: '24px', padding: '30px', boxShadow: isDark ? '0 10px 30px rgba(0,0,0,0.5)' : '0 10px 30px rgba(0,0,0,0.05)', border: isDark ? '1px solid #374151' : '1px solid #e5e7eb', transition: 'background-color 0.3s' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '15px', flexWrap: 'wrap', gap: '10px' }}>
                <h2 style={{ fontSize: '20px', fontWeight: '800', color: isDark ? '#f9fafb' : '#111827', margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <Clock color="#16a34a" /> Route Timeline
                </h2>
                {routeLegs.length > 0 && (
                  <div style={{ backgroundColor: '#16a34a', color: 'white', padding: '6px 12px', borderRadius: '50px', fontSize: '12px', fontWeight: 'bold', display: 'flex', gap: '5px' }}>
                    {totalDistKm} km • {totalTimeStr}
                  </div>
                )}
              </div>
              
              {routeOptions.length > 1 && (
                <div style={{ marginBottom: '20px' }}>
                  <p style={{ fontSize: '12px', fontWeight: 'bold', color: isDark ? '#9ca3af' : '#6b7280', textTransform: 'uppercase', marginBottom: '8px' }}>Select Route Preference:</p>
                  <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '5px' }} className="hide-scrollbar">
                    {routeOptions.map((opt, idx) => {
                      const hrs = Math.floor(opt.duration / 3600);
                      const mins = Math.floor((opt.duration % 3600) / 60);
                      const isSel = idx === selectedRouteIndex;
                      return (
                        <button key={idx} onClick={() => setSelectedRouteIndex(idx)} style={{ padding: '8px 16px', borderRadius: '12px', display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all 0.2s', border: isSel ? '2px solid #0284c7' : (isDark ? '1px solid #4b5563' : '1px solid #d1d5db'), backgroundColor: isSel ? (isDark ? '#082f49' : '#e0f2fe') : (isDark ? '#111827' : 'white'), color: isSel ? (isDark ? '#38bdf8' : '#0369a1') : (isDark ? '#d1d5db' : '#4b5563'), fontWeight: isSel ? 'bold' : '600', fontSize: '13px' }}>
                          <Route size={14} /> Route {idx + 1} ({hrs}h {mins}m)
                        </button>
                      )
                    })}
                  </div>
                </div>
              )}

              <p style={{ fontSize: '13px', color: isDark ? '#9ca3af' : '#6b7280', marginBottom: '25px', fontStyle: 'italic' }}>Customize your transport mode per leg or drag stops to reorder.</p>

              <div style={{ position: 'relative', paddingLeft: '20px', borderLeft: isDark ? '2px dashed #4b5563' : '3px dashed #d1d5db', display: 'flex', flexDirection: 'column', gap: '15px' }}>
                <div style={{ position: 'relative' }}>
                  <div style={{ position: 'absolute', left: '-30px', top: '2px', width: '16px', height: '16px', borderRadius: '50%', backgroundColor: isDark ? '#f9fafb' : '#111827', border: '3px solid white' }} />
                  <h4 style={{ fontSize: '12px', color: isDark ? '#9ca3af' : '#6b7280', fontWeight: '700', textTransform: 'uppercase' }}>Start</h4>
                  <p style={{ fontSize: '18px', fontWeight: 'bold', color: isDark ? '#f9fafb' : '#111827', textTransform: 'capitalize' }}>{origin}</p>
                </div>

                {renderLegInfo(0)}

                {itineraryStops.map((stop, index) => (
                  <React.Fragment key={stop.id}>
                    <div draggable onDragStart={(e) => handleDragStart(e, index)} onDragEnter={(e) => handleDragEnter(e, index)} onDragEnd={handleDragEnd} onDragOver={(e) => e.preventDefault()} onDrop={handleDrop} style={{ position: 'relative', backgroundColor: isDark ? '#064e3b' : '#f0fdf4', padding: '12px', borderRadius: '12px', border: isDark ? '1px solid #065f46' : '1px solid #bbf7d0', display: 'flex', gap: '10px', alignItems: 'center', cursor: 'grab' }}>
                      <div style={{ position: 'absolute', left: '-28px', top: '15px', width: '12px', height: '12px', borderRadius: '50%', backgroundColor: '#16a34a', border: '3px solid white' }} />
                      <div style={{ cursor: 'grab', color: '#9ca3af' }}><GripVertical size={20} /></div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <h4 style={{ fontSize: '11px', color: isDark ? '#a7f3d0' : '#15803d', fontWeight: 'bold', textTransform: 'uppercase', marginBottom: '2px' }}>Stop {index + 1}</h4>
                        <p style={{ fontSize: '15px', fontWeight: 'bold', color: isDark ? '#f9fafb' : '#111827', marginBottom: '2px', lineHeight: '1.2', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{stop.name}</p>
                      </div>
                      <button onClick={() => removeStop(stop.id)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '5px' }}><Trash2 size={18} /></button>
                    </div>
                    {renderLegInfo(index + 1)}
                  </React.Fragment>
                ))}

                <div style={{ position: 'relative' }}>
                  <div style={{ position: 'absolute', left: '-30px', top: '2px', width: '16px', height: '16px', borderRadius: '50%', backgroundColor: '#ea580c', border: '3px solid white' }} />
                  <h4 style={{ fontSize: '12px', color: isDark ? '#9ca3af' : '#6b7280', fontWeight: '700', textTransform: 'uppercase' }}>End</h4>
                  <p style={{ fontSize: '18px', fontWeight: 'bold', color: isDark ? '#f9fafb' : '#111827', textTransform: 'capitalize' }}>{destination}</p>
                </div>
              </div>

              <button onClick={handleFinalize} style={{ width: '100%', padding: '16px', borderRadius: '12px', backgroundColor: isDark ? '#374151' : '#111827', color: 'white', fontSize: '16px', fontWeight: 'bold', border: isDark ? '1px solid #4b5563' : 'none', cursor: 'pointer', marginTop: '30px', transition: 'all 0.3s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                Finalize Trip Details <ArrowRight size={18} />
              </button>
            </div>

            <div style={{ backgroundColor: isDark ? '#1f2937' : 'white', borderRadius: '24px', padding: '25px', boxShadow: isDark ? '0 10px 30px rgba(0,0,0,0.5)' : '0 10px 30px rgba(0,0,0,0.05)', border: isDark ? '1px solid #374151' : '1px solid #e5e7eb', transition: 'background-color 0.3s' }}>
              <h3 style={{ fontSize: '16px', fontWeight: 'bold', color: isDark ? '#f9fafb' : '#111827', marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Search size={18} color="#0284c7" /> Add a Custom Stop
              </h3>
              <div style={{ display: 'flex', gap: '10px' }}>
                <SearchAutocomplete value={customStopName} onChange={setCustomStopName} placeholder="Search exact place..." disabled={isFindingLocation} inputStyle={{ border: isDark ? '1px solid #4b5563' : '1px solid #d1d5db', padding: '12px 15px', fontSize: '14px' }} isDark={isDark} />
                <button onClick={handleAddCustomStop} disabled={isFindingLocation || !customStopName} style={{ padding: '0 20px', borderRadius: '10px', backgroundColor: '#0284c7', color: 'white', fontWeight: 'bold', border: 'none', cursor: isFindingLocation || !customStopName ? 'not-allowed' : 'pointer' }}>
                  {isFindingLocation ? <Loader2 size={16} className="animate-spin" /> : 'Add'}
                </button>
              </div>
            </div>
          </div>

          {/* COLUMN 2: MAP WIDGET & PLACES OVERVIEW */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', position: 'sticky', top: '100px', height: 'calc(100vh - 150px)' }}>
            
            <div style={{ flex: 1, backgroundColor: isDark ? '#111827' : '#e0f2fe', borderRadius: '24px', overflow: 'hidden', position: 'relative', border: isDark ? '1px solid #374151' : '1px solid #bae6fd', boxShadow: 'inset 0 0 50px rgba(0,0,0,0.05)', display: 'flex', flexDirection: 'column', minHeight: '300px' }}>
              <div style={{ backgroundColor: isDark ? '#1f2937' : 'white', padding: '12px 20px', borderBottom: isDark ? '1px solid #374151' : '1px solid #bae6fd', display: 'flex', alignItems: 'center', justifyContent: 'space-between', zIndex: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: 'bold', color: isDark ? '#f9fafb' : '#111827' }}>
                  <Layers size={18} color="#0284c7" /> Map Layout
                </div>
                <select value={currentMapStyle} onChange={(e) => setCurrentMapStyle(e.target.value)} style={{ padding: '8px 12px', borderRadius: '8px', border: isDark ? '1px solid #4b5563' : '1px solid #d1d5db', fontSize: '13px', fontWeight: '600', backgroundColor: isDark ? '#111827' : '#f9fafb', color: isDark ? 'white' : '#111827', cursor: 'pointer', outline: 'none' }}>
                  <option style={{ backgroundColor: isDark ? '#1f2937' : 'white' }} value="street">Street View (Standard)</option>
                  <option style={{ backgroundColor: isDark ? '#1f2937' : 'white' }} value="satellite">Satellite Imagery</option>
                  <option style={{ backgroundColor: isDark ? '#1f2937' : 'white' }} value="terrain">Topographic / Terrain</option>
                </select>
              </div>

              <div ref={mapContainerRef} style={{ width: '100%', flex: 1, position: 'relative' }}>
                {!window.maplibregl && <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#0284c7', fontWeight: 'bold', textAlign: 'center', padding: '20px' }}>Loading Map Engine...</div>}
              </div>
              
              <div style={{ position: 'absolute', bottom: '20px', left: '20px', backgroundColor: isDark ? 'rgba(31, 41, 55, 0.9)' : 'rgba(255,255,255,0.95)', backdropFilter: 'blur(5px)', padding: '10px 15px', borderRadius: '12px', boxShadow: '0 4px 15px rgba(0,0,0,0.1)', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', fontWeight: 'bold', color: isDark ? '#f9fafb' : '#111827', zIndex: 30, pointerEvents: 'none' }}>
                <MapIcon size={18} color="#0284c7" /> Click routes to change paths!
              </div>
            </div>

            {/* Places Overview */}
            <div style={{ flex: '0 0 auto', maxHeight: '35%', backgroundColor: isDark ? '#1f2937' : 'white', borderRadius: '24px', padding: '20px', boxShadow: isDark ? '0 10px 30px rgba(0,0,0,0.5)' : '0 10px 30px rgba(0,0,0,0.05)', border: isDark ? '1px solid #374151' : '1px solid #e5e7eb', overflowY: 'auto', transition: 'background-color 0.3s' }} className="hide-scrollbar">
              <h3 style={{ fontSize: '16px', fontWeight: '800', color: isDark ? '#f9fafb' : '#111827', marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <MapPin size={18} color="#0284c7" /> Places Overview
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                {routeCoords.start && (
                  <div>
                    <h4 style={{ fontSize: '14px', fontWeight: 'bold', color: isDark ? '#f9fafb' : '#111827' }}>{origin} <span style={{ color: isDark ? '#9ca3af' : '#6b7280', fontWeight: 'normal', fontSize: '12px' }}>(Start)</span></h4>
                    <p style={{ fontSize: '12px', color: isDark ? '#d1d5db' : '#6b7280', margin: '4px 0 0 0', lineHeight: '1.4' }}>{routeCoords.start.desc}</p>
                  </div>
                )}
                {itineraryStops.map((stop, i) => (
                  <div key={stop.id}>
                    <h4 style={{ fontSize: '14px', fontWeight: 'bold', color: isDark ? '#f9fafb' : '#111827' }}>{stop.name} <span style={{ color: isDark ? '#9ca3af' : '#6b7280', fontWeight: 'normal', fontSize: '12px' }}>(Stop {i + 1})</span></h4>
                    <p style={{ fontSize: '12px', color: isDark ? '#d1d5db' : '#6b7280', margin: '4px 0 0 0', lineHeight: '1.4' }}>{stop.desc}</p>
                  </div>
                ))}
                {routeCoords.end && (
                  <div>
                    <h4 style={{ fontSize: '14px', fontWeight: 'bold', color: isDark ? '#f9fafb' : '#111827' }}>{destination} <span style={{ color: isDark ? '#9ca3af' : '#6b7280', fontWeight: 'normal', fontSize: '12px' }}>(End)</span></h4>
                    <p style={{ fontSize: '12px', color: isDark ? '#d1d5db' : '#6b7280', margin: '4px 0 0 0', lineHeight: '1.4' }}>{routeCoords.end.desc}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* COLUMN 3: WEATHER & CATEGORIZED RECOMMENDATIONS */}
          <div style={{ overflowY: 'auto', paddingRight: '10px' }} className="hide-scrollbar">
            
            {/* DYNAMIC WEATHER WIDGET */}
            {weatherData.length > 0 && (
              <div style={{ marginBottom: '30px' }}>
                <h2 style={{ fontSize: '22px', fontWeight: '800', color: isDark ? '#f9fafb' : '#111827', marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <CloudSun color="#0284c7" /> Live Weather
                </h2>
                
                {isWeatherLoading ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#0284c7', fontSize: '14px' }}>
                    <Loader2 size={16} className="animate-spin" /> Fetching latest forecasts...
                  </div>
                ) : (
                  <div style={{ display: 'flex', gap: '10px', overflowX: 'auto', paddingBottom: '10px' }} className="hide-scrollbar">
                    {weatherData.map((w, idx) => {
                      const aqiInfo = getAQIDetails(w.aqiIndex);

                      return (
                        <div key={idx} style={{ 
                          minWidth: '130px', borderRadius: '16px', padding: '15px', border: '1px solid #e5e7eb', boxShadow: '0 4px 10px rgba(0,0,0,0.05)', textAlign: 'center', flexShrink: 0,
                          backgroundImage: `linear-gradient(to bottom, rgba(0,0,0,0.4), rgba(0,0,0,0.75)), ${getWeatherBackground(w.condition)}`,
                          backgroundSize: 'cover', backgroundPosition: 'center', color: 'white'
                        }}>
                          <p style={{ fontSize: '10px', color: 'rgba(255,255,255,0.8)', textTransform: 'uppercase', fontWeight: 'bold', margin: '0 0 4px 0' }}>{w.ptType}</p>
                          <h4 style={{ fontSize: '13px', fontWeight: 'bold', color: 'white', margin: '0 0 5px 0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{w.name}</h4>
                          {w.icon ? (
                            <img src={w.icon} alt={w.condition} style={{ width: '48px', height: '48px', margin: '0 auto', filter: 'drop-shadow(0px 2px 4px rgba(0,0,0,0.5))' }} />
                          ) : (
                            <CloudSun size={32} color="white" style={{ margin: '8px auto' }} />
                          )}
                          <div style={{ fontSize: '22px', fontWeight: '900', color: 'white', textShadow: '0px 2px 4px rgba(0,0,0,0.5)' }}>{w.temp}°C</div>
                          <p style={{ fontSize: '11px', color: 'rgba(255,255,255,0.9)', margin: '2px 0 0 0', lineHeight: '1.2', fontWeight: '500' }}>{w.condition}</p>
                          
                          {w.aqiIndex && (
                            <div style={{ marginTop: '6px', display: 'inline-flex', alignItems: 'center', gap: '4px', background: 'rgba(0,0,0,0.4)', padding: '3px 8px', borderRadius: '12px' }}>
                              <div style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: aqiInfo.color, boxShadow: `0 0 4px ${aqiInfo.color}` }}></div>
                              <span style={{ fontSize: '9px', color: 'white', fontWeight: 'bold', textTransform: 'uppercase' }}>AQI: {aqiInfo.text}</span>
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                )}

                {/* CLIMATE ALERTS */}
                {weatherAlerts.length > 0 && !isWeatherLoading && (
                  <div style={{ marginTop: '15px' }}>
                    <h3 style={{ fontSize: '15px', fontWeight: 'bold', color: '#ef4444', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <AlertTriangle size={16} /> Active Travel Advisories
                    </h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {weatherAlerts.map((alert, idx) => (
                        <div key={idx} style={{ backgroundColor: isDark ? '#450a0a' : '#fef2f2', borderLeft: '4px solid #ef4444', padding: '12px 15px', borderRadius: '8px', boxShadow: '0 2px 5px rgba(239, 68, 68, 0.1)' }}>
                          <h4 style={{ fontSize: '13px', fontWeight: 'bold', color: isDark ? '#fca5a5' : '#991b1b', margin: '0 0 4px 0' }}>{alert.location}: {alert.event}</h4>
                          <p style={{ fontSize: '12px', color: isDark ? '#fee2e2' : '#b91c1c', margin: 0, lineHeight: '1.4' }}>{alert.headline || alert.desc}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ROUTE SUGGESTIONS WITH CATEGORIES & PAGINATION */}
            <h2 style={{ fontSize: '22px', fontWeight: '800', color: isDark ? '#f9fafb' : '#111827', marginBottom: '10px' }}>Route Suggestions</h2>
            
            <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', marginBottom: '15px', paddingBottom: '5px' }} className="hide-scrollbar">
              {categories.map(cat => (
                <button 
                  key={cat}
                  onClick={() => { setActiveCategory(cat); setRecPage(0); }}
                  style={{
                    padding: '6px 14px', borderRadius: '50px', fontSize: '12px', fontWeight: 'bold', whiteSpace: 'nowrap', cursor: 'pointer', transition: 'all 0.2s', border: 'none',
                    backgroundColor: activeCategory === cat ? '#0284c7' : (isDark ? '#374151' : '#f3f4f6'),
                    color: activeCategory === cat ? 'white' : (isDark ? '#d1d5db' : '#4b5563'),
                    boxShadow: activeCategory === cat ? '0 2px 8px rgba(2, 132, 199, 0.3)' : 'none'
                  }}
                >
                  {cat}
                </button>
              ))}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
              {isSuggesting ? (
                <><Loader2 size={16} color="#0284c7" className="animate-spin" /><span style={{ fontSize: '14px', color: '#0284c7', fontWeight: '600' }}>Scanning your entire route...</span></>
              ) : (
                <span style={{ fontSize: '14px', color: isDark ? '#9ca3af' : '#6b7280' }}>Showing {filteredPlaces.length} locations.</span>
              )}
            </div>

            {/* Paginated List */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
              {currentDisplayedRecs.length > 0 ? currentDisplayedRecs.map((rec) => {
                const isAdded = itineraryStops.find(s => s.id === rec.id);
                return (
                  <div key={rec.id} style={{ 
                    backgroundColor: isDark ? '#1f2937' : 'white', borderRadius: '50px', padding: '10px', display: 'flex', alignItems: 'center', gap: '15px', border: isAdded ? '2px solid #16a34a' : (isDark ? '1px solid #374151' : '1px solid #e5e7eb'), boxShadow: isDark ? '0 4px 15px rgba(0,0,0,0.3)' : '0 4px 15px rgba(0,0,0,0.03)', transition: 'all 0.2s', opacity: isSuggesting ? 0.5 : 1
                  }}>
                    <div style={{ width: '70px', height: '70px', borderRadius: '50%', overflow: 'hidden', flexShrink: 0, border: isDark ? '2px solid #374151' : '2px solid #f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: isDark ? '#082f49' : '#e0f2fe' }}>
                      {rec.img && rec.img.includes('unsplash') ? (
                        <img src={rec.img} alt={rec.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        <rec.icon size={32} color={isDark ? '#38bdf8' : '#0284c7'} />
                      )}
                    </div>
                    <div style={{ flex: 1, overflow: 'hidden' }}>
                      
                      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '4px', color: isDark ? '#9ca3af' : '#6b7280', fontSize: '10px', fontWeight: 'bold', textTransform: 'uppercase', marginBottom: '4px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <rec.icon size={12} /> {rec.category}
                        </div>
                        {rec.isOdesseyPick && (
                          <>
                            <span style={{color: '#d1d5db'}}>•</span>
                            <span style={{ color: '#d946ef', display: 'flex', alignItems: 'center', gap: '2px' }}>
                              <Sparkles size={12} /> Odessey's Pick
                            </span>
                          </>
                        )}
                      </div>

                      <h4 style={{ fontSize: '15px', fontWeight: 'bold', color: isDark ? '#f9fafb' : '#111827', marginBottom: '2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {rec.name}
                      </h4>
                      <p style={{ fontSize: '12px', color: isDark ? '#9ca3af' : '#6b7280', margin: 0, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{rec.desc}</p>
                    </div>
                    <button onClick={() => isAdded ? removeStop(rec.id) : addStop(rec)} title={isAdded ? "Remove" : "Add"} style={{ 
                      width: '45px', height: '45px', borderRadius: '50%', flexShrink: 0, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', transition: 'all 0.2s', marginRight: '5px', backgroundColor: isAdded ? (isDark ? '#064e3b' : '#f0fdf4') : (isDark ? '#374151' : '#f3f4f6'), color: isAdded ? '#15803d' : (isDark ? 'white' : '#374151')
                    }}>
                      {isAdded ? <CheckCircle2 size={20} /> : <Plus size={20} />}
                    </button>
                  </div>
                )
              }) : !isSuggesting && (
                <div style={{ padding: '20px', textAlign: 'center', color: isDark ? '#9ca3af' : '#6b7280', backgroundColor: isDark ? '#1f2937' : 'white', borderRadius: '20px', border: isDark ? '1px dashed #4b5563' : '1px dashed #d1d5db' }}>
                  No places found in this category along the route.
                </div>
              )}

              {/* Pagination Controls */}
              {totalPages > 1 && !isSuggesting && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 20px', backgroundColor: isDark ? '#1f2937' : 'white', borderRadius: '50px', border: isDark ? '1px solid #374151' : '1px solid #e5e7eb', marginTop: '5px', boxShadow: '0 4px 10px rgba(0,0,0,0.02)', boxSizing: 'border-box' }}>
                  <button 
                    onClick={() => setRecPage(p => Math.max(0, p - 1))} 
                    disabled={recPage === 0} 
                    style={{ background: 'none', border: 'none', cursor: recPage === 0 ? 'not-allowed' : 'pointer', color: recPage === 0 ? '#4b5563' : '#0284c7', display: 'flex', alignItems: 'center', gap: '4px', fontWeight: 'bold', fontSize: '13px', padding: '0' }}
                  >
                    <ChevronLeft size={18} /> Prev
                  </button>
                  <span style={{ fontSize: '13px', fontWeight: 'bold', color: isDark ? '#d1d5db' : '#4b5563' }}>
                    Page {recPage + 1} of {totalPages}
                  </span>
                  <button 
                    onClick={() => setRecPage(p => Math.min(totalPages - 1, p + 1))} 
                    disabled={recPage === totalPages - 1} 
                    style={{ background: 'none', border: 'none', cursor: recPage === totalPages - 1 ? 'not-allowed' : 'pointer', color: recPage === totalPages - 1 ? '#4b5563' : '#0284c7', display: 'flex', alignItems: 'center', gap: '4px', fontWeight: 'bold', fontSize: '13px', padding: '0' }}
                  >
                    Next <ChevronRight size={18} />
                  </button>
                </div>
              )}
            </div>
          </div>

        </div>
      )}

      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
        .animate-spin { animation: spin 1s linear infinite; }
        @keyframes spin { 100% { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

export default RoutePlannerPage;